import React, { useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { LoginGreeting } from './LoginGreeting';
import { Users, Activity, Stethoscope, FlaskConical, Scan, Pill, Settings, LogOut, Menu, X, User as UserIcon, FileText, Clock, Baby, CreditCard } from 'lucide-react';
export const DashboardLayout: React.FC = () => {
  const {
    currentUser,
    logout
  } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  if (!currentUser) {
    return <Navigate to="/login" replace data-id="element-23" data-mp="w6l5nr1zkbp" />;
  }
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const getNavItems = () => {
    const items = [];
    if (currentUser.role === 'Administrator') {
      items.push({
        name: 'System Overview',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
      items.push({
        name: 'Billing & Invoicing',
        path: '/dashboard/billing',
        icon: CreditCard
      });
      items.push({
        name: 'Pharmacy Inventory',
        path: '/dashboard/pharmacy',
        icon: Pill
      });
      items.push({
        name: 'Staff Management',
        path: '/dashboard/staff',
        icon: Settings
      });
      items.push({
        name: 'Activity Log',
        path: '/dashboard/activity-log',
        icon: Clock
      });
    } else if (currentUser.role === 'Clinician') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else if (currentUser.role === 'Nursing') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      if (currentUser.role === 'Pharmacy' || currentUser.role === 'Pharmacy Manager') {
        items.push({
          name: currentUser.role === 'Pharmacy Manager' ? 'Pharmacist' : 'Pharmacy Dispensing',
          path: '/dashboard/pharmacy',
          icon: Pill
        });
      }
      if (currentUser.role === 'Reception') {
        items.push({
          name: 'Billing & Invoicing',
          path: '/dashboard/billing',
          icon: CreditCard
        });
      }
    }
    return items;
  };
  const navItems = getNavItems();
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Administrator':
        return 'bg-red-100 text-red-800';
      case 'Clinician':
        return 'bg-blue-100 text-blue-800';
      case 'Nursing':
        return 'bg-green-100 text-green-800';
      case 'Laboratory':
        return 'bg-yellow-100 text-yellow-800';
      case 'Radiology':
        return 'bg-purple-100 text-purple-800';
      case 'Pharmacy':
      case 'Pharmacy Manager':
        return 'bg-teal-100 text-teal-800';
      case 'Reception':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="min-h-screen bg-chrome-canvas flex" data-id="element-24" data-mp="48sdqw9mxnpe">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 flex md:hidden" data-id="element-25" data-mp="2twj8xmgdqupv">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-26" data-mp="2oyhpv1iyevee"></div>
          <div className="chrome-sidebar relative flex-1 flex flex-col max-w-xs w-full" data-id="element-27" data-mp="gp0613zn4qr0">
            <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-28" data-mp="ljmizblderd7">
              <button className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-29" data-mp="7r6fk9ok4rbr">
                <X className="h-6 w-6 text-white" data-id="element-30" data-mp="zfhtat1peu4z" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto" data-id="element-31" data-mp="14e1jx6r8su3o">
              <div className="flex-shrink-0 flex items-center px-4" data-id="element-32" data-mp="qfuw6jg0cahq">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 rounded-full bg-white object-contain" data-id="element-33" data-mp="vey020k94ao4" />
                <div className="ml-2" data-id="element-34" data-mp="3g3lql7exhdh5">
                  <span className="block text-base font-bold leading-tight text-white" data-id="element-35" data-mp="37o680m4zt160">
                    BMED Medical Centre
                  </span>
                  <span className="chrome-label text-xs font-medium uppercase tracking-wide" data-id="element-36" data-mp="33pfcgjg6drl0">
                    HIMS
                  </span>
                </div>
              </div>
              <nav className="mt-5 px-2 space-y-1" data-id="element-37" data-mp="3cotxh3dkp9y4">
                {navItems.map(item => {
              const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
              return <a key={item.name} href={item.path} onClick={e => {
                e.preventDefault();
                navigate(item.path);
                setSidebarOpen(false);
              }} className={`${isActive ? 'chrome-nav-item-active' : 'chrome-nav-item'} group flex items-center px-3 py-2 text-base font-medium rounded-md`} data-id="element-38" data-mp="2txjc11qm9pdm">
                      <item.icon className="mr-4 flex-shrink-0 h-6 w-6" data-id="element-39" data-mp="a2pp5wve24if" />
                      {item.name}
                    </a>;
            })}
              </nav>
            </div>
            <div className="chrome-divider flex-shrink-0 border-t p-4" data-id="element-40" data-mp="iyym94vw8r1e">
              <div className="flex items-center" data-id="element-41" data-mp="3idtwsvw87kx8">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20" data-id="element-42" data-mp="3nb2j98oeqb4a">
                  <UserIcon className="h-6 w-6" data-id="element-43" data-mp="3rcfnxffrk89m" />
                </div>
                <div className="ml-3" data-id="element-44" data-mp="3rfa610bb1gzv">
                  <p className="text-base font-medium" data-id="element-45" data-mp="6wbub9ui6zl">
                    {currentUser.fullName}
                  </p>
                  <p className="chrome-label text-sm font-medium" data-id="element-46" data-mp="16lwz7skt1guk">
                    {currentUser.role}
                  </p>
                </div>
              </div>
              <p className="chrome-label mt-4 text-xs" data-id="element-47" data-mp="1lq9higrc2hbt">
                Caring · Healing · Leading
              </p>
            </div>
          </div>
        </div>}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 print:hidden" data-id="element-48" data-mp="1bzesj4mpd3mo">
        <div className="flex flex-col w-64" data-id="element-49" data-mp="1bxkbw2igrxoe">
          <div className="chrome-sidebar flex-1 flex flex-col min-h-0" data-id="element-50" data-mp="17xa5wpuo979j">
            <div className="flex-1 flex flex-col overflow-y-auto pb-4" data-id="element-51" data-mp="12yyre1tfm8kz">
              <div className="chrome-divider flex flex-shrink-0 items-center border-b px-4 py-5" data-id="element-52" data-mp="12wvv501orj7t">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 rounded-full bg-white object-contain" data-id="element-53" data-mp="y2i75ipcsed6" />
                <div className="ml-3" data-id="element-54" data-mp="2ndshkct6zp4a">
                  <span className="block text-base font-bold leading-tight text-white" data-id="element-55" data-mp="2ncr2zycywkht">
                    BMED Medical Centre
                  </span>
                  <span className="chrome-label text-xs font-medium uppercase tracking-wide" data-id="element-56" data-mp="32njgawryd5fw">
                    HIMS
                  </span>
                </div>
              </div>
              <nav className="mt-6 flex-1 space-y-1 px-3" data-id="element-57" data-mp="2sx8bz94cwu8h">
                {navItems.map(item => {
                const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
                return <a key={item.name} href={item.path} onClick={e => {
                  e.preventDefault();
                  navigate(item.path);
                }} className={`${isActive ? 'chrome-nav-item-active' : 'chrome-nav-item'} group flex items-center rounded-full px-4 py-2.5 text-sm font-medium`} data-id="element-58" data-mp="2nyncxd0ucglm">
                      <item.icon className="mr-3 flex-shrink-0 h-5 w-5" data-id="element-59" data-mp="n8zinza2lqa6" />
                      {item.name}
                    </a>;
              })}
              </nav>
            </div>
            <div className="chrome-divider flex-shrink-0 border-t p-4" data-id="element-60" data-mp="w1b04vn7i3e0">
              <div className="flex items-center" data-id="element-61" data-mp="w3dxy1ulp0ta">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20" data-id="element-62" data-mp="r9rsouqrnz7i">
                  <UserIcon className="h-5 w-5" data-id="element-63" data-mp="15yo1wv4k7i9p" />
                </div>
                <div className="ml-3" data-id="element-64" data-mp="110w5powvt9zp">
                  <p className="text-sm font-medium" data-id="element-65" data-mp="10zun8zdjdl7y">
                    {currentUser.fullName}
                  </p>
                  <p className="chrome-label mt-0.5 text-xs font-medium" data-id="element-66" data-mp="1ezm375nm5bol">
                    {currentUser.role}
                  </p>
                </div>
              </div>
              <p className="chrome-label mt-4 text-xs" data-id="element-67" data-mp="3gsjtoutx3s6d">
                Caring · Healing · Leading
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden" data-id="element-68" data-mp="2gs0604a44vm1">
        <header className="chrome-header flex items-center justify-between gap-3 border-b border-white/10 px-3 py-3 sm:px-6 print:hidden" data-id="element-69" data-mp="2cp3t8u797tq0">
          <button className="chrome-header-button inline-flex h-10 w-10 items-center justify-center rounded-md md:hidden" onClick={() => setSidebarOpen(true)} data-id="element-70" data-mp="27r2akadqpwf2">
            <span className="sr-only" data-id="element-71" data-mp="30eofpn2m9941">Open sidebar</span>
            <Menu className="h-6 w-6" data-id="element-72" data-mp="2vfl8bvz5yqsw" />
          </button>
          <LoginGreeting user={currentUser} data-id="element-73" data-mp="2vgwkok6od6vm" />
          <div className="ml-auto flex shrink-0 items-center gap-3" data-id="element-74" data-mp="2qov1eeb3afw3">
            <div className="hidden text-right sm:block" data-id="element-75" data-mp="2qn1wb3g4k3q7">
              <p className="text-sm font-medium leading-tight" data-id="element-76" data-mp="2lpiiu5r93uzb">
                {currentUser.fullName}
              </p>
              <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getRoleColor(currentUser.role)}`} data-id="element-77" data-mp="3j70i29t9sae6">
                {currentUser.role}
              </span>
            </div>
            <span className={`rounded-full px-2 py-1 text-xs font-medium sm:hidden ${getRoleColor(currentUser.role)}`} data-id="element-78" data-mp="3qoyjdr1asf6b">
              {currentUser.role}
            </span>
            <button onClick={handleLogout} className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-medium text-chrome-header-ink hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-79" data-mp="4hqnrgg6jmnh">
              <LogOut className="mr-2 h-4 w-4" data-id="element-80" data-mp="i8vetu5hf6m7" />
              Sign out
            </button>
          </div>
        </header>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none" data-id="element-81" data-mp="n7ggzz4ao0da">
          <div className="py-6" data-id="element-82" data-mp="s8dva0m6oa9i">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-83" data-mp="s95oj4sb5vwm">
              <Outlet data-id="element-84" data-mp="w8ehw8jfrrrn" />
            </div>
          </div>
        </main>
      </div>
    </div>;
};